/**
 * The table view (spec M1-B4).
 *
 * It assembles the board chrome — the view tabs, the toolbar, the panes, the menus, the grid,
 * the bulk bar and the toast — and owns nothing else: the store holds the state, the data
 * source holds the data, and the registry holds everything type-specific.
 *
 * Vertical geometry, re-measured against monday on all ten parity boards (P-024,
 * `discovery/ui-reference/parity/measurements.json`), relative to the board region's top:
 * title band 0 -> 58 (the 30 px title 19 px in), view tabs 58 -> 92 (34 px), toolbar 92 -> 124
 * (32 px, no air above it), grid from 156 (32 px of air). In the 1440 x 900 viewport that is
 * title y 68, tabs y 107, toolbar y 141 and the grid at y 205. Horizontally the chrome starts
 * at x 389 (38 px into the region) and the grid at x 391 (40 px).
 */
import type { ColumnId, GroupId, ItemId, ViewId } from '@elizmonday/shared';
import { PageSkeleton } from '@elizmonday/ui';
import { useEffect, useState } from 'react';
import { onColumnSettingsRequested } from '../columns/settings-request';
import { useCellActions } from './cell-actions';
import { onBoardPrintRequested } from '../print-request';
import { ColumnSettingsHost } from './ColumnSettingsHost';
import { Grid } from './Grid';
import { MultiSelectBar } from './MultiSelectBar';
import { PresenceStrip } from './PresenceStrip';
import { Toolbar } from './Toolbar';
import { UndoToast } from './UndoToast';
import { useTable, useTableActions } from './context';
import { AddColumnGallery } from './menus/AddColumnGallery';
import { ColumnDeleteConfirm, ColumnRenameDialog } from './menus/ColumnDialogs';
import { ColumnMenu } from './menus/ColumnMenu';
import { GroupMenu } from './menus/GroupMenu';
import { ItemMenu } from './menus/ItemMenu';
import { MoreMenu } from './menus/MoreMenu';
import { MoveItemsDialog } from './menus/MoveItemsDialog';
import { SummaryMenu } from './menus/SummaryMenu';
import { CardList, LayoutToggle, usePhone, usePhoneBoardLayout } from './phone';
import { AdvancedFilterPane } from './panes/AdvancedFilter';
import { GroupByPane } from './panes/GroupBy';
import { HidePane } from './panes/Hide';
import { PersonFilterPane } from './panes/PersonFilter';
import { QuickFilterPane } from './panes/QuickFilter';
import { SortPane } from './panes/Sort';
import './table.css';

export interface TableViewProps {
  activeViewId: ViewId;
  onSelectView?(viewId: ViewId): void;
  /** P-266: `options.tab` names the item-card tab the gesture asks for (the row's bubble: Updates). */
  onOpenItem?(itemId: ItemId, options?: { tab?: 'updates' }): void;
  /** `false` inside the `/__ui` harness, where the board chrome is the page. */
  showBoardHeader?: boolean;
  /**
   * `false` on the product route, where **B1**'s `<ViewTabsBar/>` renders the real bar with
   * create / rename / duplicate / delete / reorder. The read-only strip below is what the
   * fixture harness still uses, so `/__ui/board` needs no api for its tabs.
   */
  showViewTabs?: boolean;
}

export function TableView({
  activeViewId,
  onSelectView,
  onOpenItem,
  showBoardHeader = true,
  showViewTabs = true,
}: TableViewProps) {
  const status = useTable((state) => state.status);
  const error = useTable((state) => state.error);
  const payload = useTable((state) => state.payload);
  const pane = useTable((state) => state.pane);
  /** P-326: the nested `+` opens the gallery under itself, not under the board's `+`. */
  const paneAnchor = useTable((state) => state.paneAnchor);
  const menu = useTable((state) => state.menu);
  /** P-054: the row menu's `Move to ▸ Move to board` picker, owned here so the menu that
   *  opened it cannot unmount it. */
  const movingItemIds = useTable((state) => state.movingItemIds);
  /**
   * P-080 / P-087: the column rename dialog and the delete confirm, owned here for the same
   * reason — the row that opens either closes the menu that would otherwise render it.
   */
  const renamingColumnId = useTable((state) => state.renamingColumnId);
  const deletingColumnId = useTable((state) => state.deletingColumnId);
  const actions = useTableActions();

  // A sorted view disables row drag with an explanatory tooltip (F6, ASSUMED Q171).
  const sorted = useTable((state) => state.effective.sort.length > 0);
  const accessRevoked = useTable((state) => state.accessRevoked);
  const removedState = useTable((state) => state.removedState);

  /**
   * P-042 §3 (F9-D29): below `breakpoints.phone` the board reads as a card list by default and
   * the grid is one tap away. Both hooks are inert above 767 px — `usePhone()` is `false`, the
   * toggle is not rendered and `<Grid/>` is what mounts — so the captured desktop geometry
   * cannot move (M0-F9 D-0).
   */
  const phone = usePhone();
  const [phoneLayout, setPhoneLayout] = usePhoneBoardLayout();
  const phoneCards = phone && phoneLayout === 'cards';

  /** `✎ Edit Labels` inside a cell editor opens the type's settings dialog (`P-003`). */
  const [settingsColumnId, setSettingsColumnId] = useState<ColumnId | null>(null);
  useEffect(
    () => onColumnSettingsRequested((columnId) => setSettingsColumnId(columnId as ColumnId)),
    [],
  );

  /** P-090: the board menu's `Print board`, answered with every row in the DOM. */
  useEffect(() => onBoardPrintRequested(() => void actions.printBoard()), [actions]);

  /**
   * P-128: the button, the timer and the file uploader. Their editors dispatch a request and
   * this is what receives it — the twin of `onColumnSettingsRequested` above.
   */
  useCellActions();

  useEffect(() => {
    if (status !== 'ready') return;
    // §8.5: the personal (unsaved) state is written debounced; the store already did the write,
    // this only makes sure a freshly mounted view starts from it.
  }, [status]);

  if (status === 'loading') return <PageSkeleton variant="board" data-testid="board-skeleton" />;
  if (status === 'error' || !payload) {
    return (
      /* P-308: named, so a verifier can prove it is **not** on screen. */
      <div className="ez-board__error" role="alert" data-testid="board-error">
        {error ?? 'The board could not be loaded'}
      </div>
    );
  }

  /*
   * The board went away under the viewer, or their access to it did (M0-F5 §8, E7/E8). Both
   * are full-screen panels rather than a toast: continuing to show rows the viewer may no
   * longer read, or a board that is in the trash, is worse than saying so.
   */
  if (accessRevoked) {
    return (
      <div className="ez-board__gone" role="alert" data-testid="board-access-revoked">
        <h2>You no longer have access to this board</h2>
        <p>Someone changed who can see it while you had it open.</p>
        <a href="/">Back to your boards</a>
      </div>
    );
  }
  if (removedState) {
    return (
      <div className="ez-board__gone" role="alert" data-testid="board-removed">
        <h2>{removedState === 'archived' ? 'This board was archived' : 'This board was removed'}</h2>
        <p>It is no longer in your sidebar.</p>
        <a href="/">Back to your boards</a>
      </div>
    );
  }

  return (
    <div className="ez-board" data-testid="table-view" data-sorted={sorted || undefined}>
      {showBoardHeader ? (
        <header className="ez-board__header">
          <h1 className="ez-board__title">{payload.board.name}</h1>
          <PresenceStrip />
        </header>
      ) : null}

      {showViewTabs ? (
      <nav className="ez-board__tabs" aria-label="Board views">
        <div className="ez-board__tablist" role="tablist" aria-label="Board views">
          {payload.views.map((view) => (
            <button
              key={view.id}
              type="button"
              role="tab"
              className="ez-board-tab"
              aria-selected={view.id === activeViewId}
              data-testid={`view-tab-${view.id}`}
              onClick={() => onSelectView?.(view.id)}
            >
              {view.name}
            </button>
          ))}
          <button type="button" className="ez-board-tab ez-board-tab--all" role="tab" aria-selected={false}>
            All
          </button>
        </div>
        {/* Outside the `tablist`: it adds a view, it is not one (A2). */}
        <button type="button" className="ez-board-tab ez-board-tab--add" aria-label="Add view" disabled>
          +
        </button>
      </nav>
      ) : null}

      {/*
        * On the product route the header is B1's, so the strip rides with the toolbar — P-024:
        * *inside* the toolbar row, not under it. As a row of its own it added 33 px between the
        * toolbar and the grid, and monday leaves exactly 32 there in total.
        */}
      <div className="ez-board__toolbar-row">
        <Toolbar />
        {phone ? <LayoutToggle layout={phoneLayout} onChange={setPhoneLayout} /> : null}
        {showBoardHeader ? null : <PresenceStrip />}
      </div>

      <div className="ez-board__panes">
        {pane === 'quick-filter' ? (
          <QuickFilterPane onSwitchToAdvanced={() => actions.openPane('advanced-filter')} />
        ) : null}
        {pane === 'advanced-filter' ? (
          <AdvancedFilterPane onSwitchToQuick={() => actions.openPane('quick-filter')} />
        ) : null}
        {pane === 'sort' ? <SortPane /> : null}
        {pane === 'hide' ? <HidePane /> : null}
        {pane === 'group-by' ? <GroupByPane /> : null}
        {pane === 'person' ? <PersonFilterPane /> : null}
        {/* `search` is not a pane: the toolbar control expands in place (B6 §4.8). */}
      </div>

      {phoneCards ? (
        <CardList onOpenItem={(itemId) => onOpenItem?.(itemId)} />
      ) : (
        <Grid onOpenItem={(itemId, options) => onOpenItem?.(itemId, options)} />
      )}

      {pane === 'add-column' ? (
        <AddColumnGallery x={paneAnchor?.x ?? 0} y={paneAnchor?.y ?? 0} />
      ) : null}

      {menu?.kind === 'column' ? (
        <ColumnMenu columnId={menu.targetId as ColumnId} x={menu.x} y={menu.y} />
      ) : null}
      {menu?.kind === 'column-summary' ? (
        <SummaryMenu columnId={menu.targetId as ColumnId} x={menu.x} y={menu.y} />
      ) : null}
      {menu?.kind === 'group' ? <GroupMenu groupId={menu.targetId as GroupId} x={menu.x} y={menu.y} /> : null}
      {menu?.kind === 'item' ? (
        <ItemMenu
          itemId={menu.targetId as ItemId}
          x={menu.x}
          y={menu.y}
          onOpenItem={() => onOpenItem?.(menu.targetId as ItemId)}
          contextMenu={menu.context === true}
        />
      ) : null}
      {menu?.kind === 'board' ? <MoreMenu x={menu.x} y={menu.y} /> : null}

      {renamingColumnId ? <ColumnRenameDialog columnId={renamingColumnId} /> : null}
      {deletingColumnId ? (
        <ColumnDeleteConfirm key={String(deletingColumnId)} columnId={deletingColumnId} />
      ) : null}

      {movingItemIds.length > 0 ? (
        <MoveItemsDialog
          itemIds={movingItemIds.map(String)}
          onClose={() => actions.setMovingItems([])}
        />
      ) : null}

      {settingsColumnId ? (
        <ColumnSettingsHost columnId={settingsColumnId} onClose={() => setSettingsColumnId(null)} />
      ) : null}

      <MultiSelectBar />
      <UndoToast />
    </div>
  );
}
